/**
 * Words for an order's progress as the customer sees it (order tracking,
 * ROADMAP.md phase 2). Pure, so the copy is tested here rather than through
 * the DOM. The admin pages' words are in `rollDetails.ts`.
 */
import type {
  ExpectedRolls,
  NegativesPlan,
  NegativesState,
  OrderPhase,
  Parcel,
  RollFormat,
  RollGroupTracking,
  RollKind,
  RollProcess,
  RollStep,
} from '../types/roll';
import { shortDate } from './rollDetails';

export const PHASE_LABELS: Record<OrderPhase, string> = {
  awaiting_film: 'Awaiting film',
  received: 'Film received',
  in_lab: 'In the lab',
  developed: 'Developed',
  ready: 'Ready',
  expired: 'Expired',
  cancelled: 'Cancelled',
  pending: 'Pending',
};

const LONG_DAY = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });

/** "12 December 2026", as the emails write it. */
export function longDate(iso: string): string {
  return LONG_DAY.format(new Date(iso));
}

/** The order list's second line: where the order's negatives are, or null. */
export function negativesLine(state: NegativesState | undefined): string | null {
  switch (state?.state) {
    case 'posted':
      return `Negatives posted ${shortDate(state.parcel.shipped_at)}`;
    case 'held':
      return `Negatives held until ${shortDate(state.until)}`;
    case 'ready_to_collect':
      return 'Negatives ready to collect';
    case 'collected':
      return `Negatives collected ${shortDate(state.at)}`;
    case 'awaiting_dispatch':
      return 'Negatives to be posted back';
    case 'awaiting_disposal':
      return 'Negatives to be disposed of, as you asked';
    case 'disposed':
      return `Negatives disposed of ${shortDate(state.at)}, as you asked`;
    case 'returned_with_order':
      return 'Negatives sent with another of your orders';
    default:
      return null;
  }
}

const WILL_HAPPEN: Record<Exclude<NegativesPlan, 'unknown'>, string> = {
  post_back: "We'll post your negatives back when we've finished.",
  hold: "We'll keep your negatives for up to 90 days, to send with a companion print or new film order.",
  collect: "When we've finished, your negatives will be ready to collect from the lab in Manningtree.",
  dispose: "As you asked, we'll dispose of your negatives when we've finished.",
};

/** A roll's negatives, in a sentence under its progress, or null when not known. */
export function rollNegatives(state: NegativesState): string | null {
  switch (state.state) {
    case 'pending':
      return state.plan === 'unknown' ? null : WILL_HAPPEN[state.plan];
    case 'awaiting_dispatch':
      return "We'll post your negatives back soon.";
    case 'posted':
      return `We posted your negatives back on ${longDate(state.parcel.shipped_at)}.`;
    case 'held':
      return state.overdue
        ? `We held your negatives until ${longDate(state.until)}.`
        : `We're keeping your negatives until ${longDate(state.until)}, to send with a companion print or new film order.`;
    case 'ready_to_collect':
      return 'Your negatives are ready to collect from the lab in Manningtree.';
    case 'collected':
      return `You collected your negatives on ${longDate(state.at)}.`;
    case 'awaiting_disposal':
      return "As you asked, we'll dispose of your negatives.";
    case 'disposed':
      return `As you asked, we disposed of your negatives on ${longDate(state.at)}.`;
    case 'returned_with_order':
      return 'We sent your negatives with another of your orders.';
    default:
      return null;
  }
}

const STEP_LABELS: Record<Exclude<RollStep['key'], 'negatives'>, string> = {
  received: 'Received',
  developed: 'Developed',
  scans_ready: 'Scans ready',
};

const NEGATIVES_STEP: Partial<Record<NegativesState['state'], string>> = {
  posted: 'Posted back',
  awaiting_dispatch: 'Posted back',
  held: 'Held for you',
  ready_to_collect: 'Ready to collect',
  collected: 'Collected',
  awaiting_disposal: 'Disposed of',
  disposed: 'Disposed of',
  returned_with_order: 'Sent with another order',
};

const NEGATIVES_PLANNED: Record<Exclude<NegativesPlan, 'unknown'>, string> = {
  post_back: 'Posted back',
  hold: 'Held for you',
  collect: 'Ready to collect',
  dispose: 'Disposed of',
};

/** A step's name. The negatives step is named for what happens to them. */
export function stepLabel(key: RollStep['key'], negatives: NegativesState): string {
  if (key !== 'negatives') return STEP_LABELS[key];
  if (negatives.state === 'pending' && negatives.plan !== 'unknown') return NEGATIVES_PLANNED[negatives.plan];
  return NEGATIVES_STEP[negatives.state] ?? 'Negatives';
}

/** The roll's latest news, for its header: "Scans ready 5 Oct 2026". */
export function rollStatus(tracking: RollGroupTracking): string | null {
  const done = tracking.steps.filter((s) => s.state === 'done');
  const last = done[done.length - 1];
  if (!last) return null;
  const label = stepLabel(last.key, tracking.negatives);
  return last.at ? `${label} ${shortDate(last.at)}` : label;
}

const PROCESS_WORDS: Record<RollProcess, string> = {
  c41: 'colour (C-41)',
  bw: 'black and white',
  e6: 'slide (E-6)',
  other: '',
};

const FORMAT_WORDS: Record<RollFormat, string> = {
  '35mm': '35mm',
  '120': '120',
  half_frame: 'half frame',
  other: '',
};

const SERVICE_WORDS: Record<RollKind, string> = {
  develop_and_scan: 'develop and scan',
  develop_only: 'develop only',
  scan_only: 'scan only',
  unknown: '',
};

/** One line of what's on its way: "2 × 35mm colour (C-41), develop and scan". */
export function expectedLine(expected: ExpectedRolls): string {
  const film = [expected.format && FORMAT_WORDS[expected.format], expected.process && PROCESS_WORDS[expected.process]]
    .filter(Boolean)
    .join(' ');
  const what = [film || 'film', SERVICE_WORDS[expected.kind]].filter(Boolean).join(', ');
  return `${expected.quantity} × ${what}`;
}

/** A parcel, in a line: "Posted 6 October 2026 · Royal Mail AB123456789GB". */
export function parcelLine(parcel: Parcel): string {
  const via = [parcel.carrier, parcel.number].filter(Boolean).join(' ');
  return `Posted ${longDate(parcel.shipped_at)}${via ? ` · ${via}` : ''}`;
}
